import { useMemo, useState } from 'react';
import { differenceInCalendarDays, startOfDay, subDays } from 'date-fns';

import {
  OpcaoRmv,
  SalvarSimulacaoAntigaRequest,
  TipoRodada,
  WindxSimulacaoAntiga,
} from 'helper/windx';

export interface SimulacaoAntigaFormState {
  nome: string;
  data: Date | undefined;
  tipoRodada: TipoRodada;
  idsModelos: number[];
  opcoesRmv: OpcaoRmv[];
}

export interface SimulacaoAntigaFormApi {
  form: SimulacaoAntigaFormState;
  setNome: (v: string) => void;
  setData: (v: Date | undefined) => void;
  setTipoRodada: (v: TipoRodada) => void;
  setIdsModelos: (v: number[]) => void;
  setOpcoesRmv: (v: OpcaoRmv[]) => void;
  reset: () => void;
  isValid: boolean;
  errors: string[];
  payload: SalvarSimulacaoAntigaRequest | undefined;
}

const LIMITE_NOME = 30;
const LIMITE_DIAS_MIN = 1; // D-1
const LIMITE_DIAS_MAX = 14; // D-14

function initialFromSim(
  initial: WindxSimulacaoAntiga | undefined,
): SimulacaoAntigaFormState {
  if (!initial) {
    return {
      nome: '',
      data: undefined,
      tipoRodada: 'final',
      idsModelos: [],
      opcoesRmv: ['com_rmv'],
    };
  }
  // FE-WINDX-043: prefill em modo edit resolve dias_atras -> data (re-calculada
  // com hoje atual). A data eh meramente visual; o que importa para o BE eh o
  // offset relativo, recomputado no submit a partir de form.data.
  return {
    nome: initial.nome,
    data: subDays(startOfDay(new Date()), initial.dias_atras),
    tipoRodada: initial.tipo_rodada,
    idsModelos: [...initial.id_modelos],
    opcoesRmv: [...initial.opcoes_rmv],
  };
}

/**
 * Hook de estado LOCAL do form do <ModalCadastrar> (NAO Redux).
 *
 * Conversao chave: o usuario escolhe uma DATA no calendario (UX familiar);
 * o hook converte para `dias_atras` (offset relativo) no payload. Em modo
 * edit, faz o inverso: `dias_atras` salvo -> data preenchida no calendario.
 *
 * Validacoes (todas client-side):
 *   - nome: 1..30 chars (CHECK no BD + Pydantic + counter visual)
 *   - data: D-14 <= data <= D-1 (alinhado com LIMITE_DIAS_SIMULACAO_ANTIGA)
 *   - idsModelos: nao vazio
 *   - opcoesRmv: nao vazio (min 1)
 *
 * Validacao mais forte (ids invalidos no painel) eh feita no BE; aqui apenas
 * gating do botao Salvar.
 */
export default function useSimulacaoAntigaForm(
  initial?: WindxSimulacaoAntiga,
): SimulacaoAntigaFormApi {
  const [form, setForm] = useState<SimulacaoAntigaFormState>(() => initialFromSim(initial));

  const setNome = (v: string) => setForm((f) => ({ ...f, nome: v }));
  const setData = (v: Date | undefined) => setForm((f) => ({ ...f, data: v }));
  const setTipoRodada = (v: TipoRodada) => setForm((f) => ({ ...f, tipoRodada: v }));
  const setIdsModelos = (v: number[]) => setForm((f) => ({ ...f, idsModelos: v }));
  const setOpcoesRmv = (v: OpcaoRmv[]) => setForm((f) => ({ ...f, opcoesRmv: v }));
  const reset = () => setForm(initialFromSim(initial));

  const errors = useMemo<string[]>(() => {
    const errs: string[] = [];
    const nomeTrim = form.nome.trim();
    if (nomeTrim.length === 0) errs.push('Nome obrigatório.');
    if (nomeTrim.length > LIMITE_NOME) errs.push(`Nome com no máximo ${LIMITE_NOME} caracteres.`);
    if (!form.data) {
      errs.push('Data obrigatória.');
    } else {
      const diasAtras = differenceInCalendarDays(
        startOfDay(new Date()),
        startOfDay(form.data),
      );
      if (diasAtras < LIMITE_DIAS_MIN || diasAtras > LIMITE_DIAS_MAX) {
        errs.push(`Data deve estar entre D-${LIMITE_DIAS_MAX} e D-${LIMITE_DIAS_MIN}.`);
      }
    }
    if (form.idsModelos.length === 0) errs.push('Selecione ao menos um modelo.');
    if (form.opcoesRmv.length === 0) errs.push('Selecione ao menos uma opção de RMV.');
    return errs;
  }, [form.nome, form.data, form.idsModelos, form.opcoesRmv]);

  const isValid = errors.length === 0;

  const payload = useMemo<SalvarSimulacaoAntigaRequest | undefined>(() => {
    if (!isValid || !form.data) return undefined;
    const diasAtras = differenceInCalendarDays(
      startOfDay(new Date()),
      startOfDay(form.data),
    );
    return {
      nome: form.nome.trim(),
      dias_atras: diasAtras,
      tipo_rodada: form.tipoRodada,
      id_modelos: form.idsModelos,
      opcoes_rmv: form.opcoesRmv,
    };
  }, [isValid, form]);

  return {
    form,
    setNome,
    setData,
    setTipoRodada,
    setIdsModelos,
    setOpcoesRmv,
    reset,
    isValid,
    errors,
    payload,
  };
}
